/**
 * Supplier Edit Page
 *
 * Detailed supplier management with products and orders.
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { COUNTRIES } from '../../lib/countries';
import {
  ArrowLeft,
  Save,
  Truck,
  Package,
  ShoppingCart,
  Mail,
  Phone,
  Globe,
  Building2,
  Zap,
  Clock,
  CheckCircle,
  AlertCircle,
  Plus,
  ExternalLink,
  Unlink,
} from 'lucide-react';

interface Supplier {
  id: string;
  name: string;
  code: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  country: string | null;
  supplier_type: string;
  status: string;
  integration_type: string | null;
  api_endpoint: string | null;
  has_api_credentials: boolean;
  webhook_url: string | null;
  auto_order_enabled: boolean;
  auto_order_delay_minutes: number;
  order_prefix: string | null;
  order_email_template: string | null;
  currency: string;
  payment_terms: string | null;
  default_markup_type: string;
  default_markup_value: number;
  default_shipping_method: string | null;
  average_processing_days: number;
  average_shipping_days: number;
  ships_from_country: string | null;
  ships_to_countries: string[];
  total_orders: number;
  successful_orders: number;
  failed_orders: number;
  average_fulfillment_days: number | null;
  rating: number | null;
  notes: string | null;
  tags: string[];
  product_count: number;
  recent_orders: Array<{
    id: string;
    supplier_order_number: string | null;
    status: string;
    total_cost: number;
    created_at: string;
  }>;
  created_at: string;
}

interface SupplierProduct {
  id: string;
  product_id: string;
  supplier_sku: string | null;
  supplier_cost: number;
  shipping_cost: number;
  is_primary: boolean;
  auto_fulfill: boolean;
  status: string;
  product: {
    id: string;
    name: string;
    slug: string;
    price: number;
    images: string[];
    status: string;
  };
  profit_margin: number | null;
}

interface StoreProduct {
  id: string;
  name: string;
  price: number;
  images: string[];
  status: string;
}

const TABS = ['details', 'products', 'orders', 'settings'] as const;
type Tab = typeof TABS[number];

export default function SupplierEditPage() {
  const { t } = useTranslation('store');
  const { storeId, supplierId } = useParams<{ storeId: string; supplierId: string }>();

  const [supplier, setSupplier] = useState<Supplier | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('details');

  // Products tab state
  const [supplierProducts, setSupplierProducts] = useState<SupplierProduct[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [availableProducts, setAvailableProducts] = useState<StoreProduct[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<string>('');
  const [linkFormData, setLinkFormData] = useState({
    supplier_sku: '',
    supplier_cost: '',
    shipping_cost: '',
    is_primary: true,
    auto_fulfill: true,
  });

  // Form state
  const [formData, setFormData] = useState<Partial<Supplier>>({});

  const loadSupplier = useCallback(async () => {
    if (!storeId || !supplierId) return;

    try {
      setLoading(true);
      const response = await api.get(`/api/ecommerce/stores/${storeId}/suppliers/${supplierId}`);
      const data = await response.json() as { supplier: Supplier };
      setSupplier(data.supplier);
      setFormData(data.supplier);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load supplier');
    } finally {
      setLoading(false);
    }
  }, [storeId, supplierId]);

  const loadSupplierProducts = useCallback(async () => {
    if (!storeId || !supplierId) return;

    try {
      setLoadingProducts(true);
      const response = await api.get(`/api/ecommerce/stores/${storeId}/suppliers/${supplierId}/products`);
      const data = await response.json() as { supplier_products: SupplierProduct[] };
      setSupplierProducts(data.supplier_products || []);
    } catch (err) {
      console.error('Failed to load supplier products:', err);
    } finally {
      setLoadingProducts(false);
    }
  }, [storeId, supplierId]);

  const loadAvailableProducts = useCallback(async () => {
    if (!storeId) return;

    try {
      const response = await api.get(`/api/ecommerce/stores/${storeId}/products?limit=100`);
      const data = await response.json() as { products: StoreProduct[] };
      // Filter out products already linked to this supplier
      const linkedProductIds = new Set(supplierProducts.map(sp => sp.product_id));
      setAvailableProducts((data.products || []).filter(p => !linkedProductIds.has(p.id)));
    } catch (err) {
      console.error('Failed to load products:', err);
    }
  }, [storeId, supplierProducts]);

  useEffect(() => {
    loadSupplier();
  }, [loadSupplier]);

  useEffect(() => {
    if (activeTab === 'products') {
      loadSupplierProducts();
    }
  }, [activeTab, loadSupplierProducts]);

  useEffect(() => {
    if (showLinkModal) {
      loadAvailableProducts();
    }
  }, [showLinkModal, loadAvailableProducts]);

  const handleSave = async () => {
    if (!storeId || !supplierId) return;

    try {
      setSaving(true);
      setError(null);
      await api.put(`/api/ecommerce/stores/${storeId}/suppliers/${supplierId}`, formData);
      setSuccess(t('suppliers.saved', 'Supplier saved successfully'));
      loadSupplier();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save supplier');
    } finally {
      setSaving(false);
    }
  };

  const handleLinkProduct = async () => {
    if (!storeId || !supplierId || !selectedProduct) return;

    try {
      setSaving(true);
      await api.post(`/api/ecommerce/stores/${storeId}/suppliers/${supplierId}/products`, {
        product_id: selectedProduct,
        supplier_sku: linkFormData.supplier_sku || null,
        supplier_cost: parseInt(linkFormData.supplier_cost) || 0,
        shipping_cost: parseInt(linkFormData.shipping_cost) || 0,
        is_primary: linkFormData.is_primary,
        auto_fulfill: linkFormData.auto_fulfill,
      });
      setShowLinkModal(false);
      setSelectedProduct('');
      setLinkFormData({ supplier_sku: '', supplier_cost: '', shipping_cost: '', is_primary: true, auto_fulfill: true });
      loadSupplierProducts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to link product');
    } finally {
      setSaving(false);
    }
  };

  const handleUnlinkProduct = async (productId: string) => {
    if (!storeId || !supplierId) return;

    try {
      await api.delete(`/api/ecommerce/stores/${storeId}/suppliers/${supplierId}/products/${productId}`);
      loadSupplierProducts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unlink product');
    }
  };

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: supplier?.currency || 'USD',
    }).format(cents / 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!supplier) {
    return (
      <div className="p-6 text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          {t('suppliers.notFound', 'Supplier not found')}
        </h2>
        <Link
          to={`/store/${storeId}/suppliers`}
          className="text-blue-600 hover:text-blue-700"
        >
          {t('suppliers.backToList', 'Back to suppliers')}
        </Link>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link
            to={`/store/${storeId}/suppliers`}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Building2 className="w-6 h-6" />
              {supplier.name}
            </h1>
            <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
              {supplier.code && <span>({supplier.code})</span>}
              <span className="capitalize">{supplier.supplier_type.replace('_', ' ')}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                supplier.status === 'active' ? 'bg-green-100 text-green-800' :
                supplier.status === 'inactive' ? 'bg-gray-100 text-gray-800' :
                'bg-yellow-100 text-yellow-800'
              }`}>
                {supplier.status}
              </span>
            </div>
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          {saving ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 flex items-center gap-2">
          <CheckCircle className="w-5 h-5" />
          {success}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Package className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <div className="text-2xl font-bold">{supplier.product_count}</div>
              <div className="text-sm text-gray-500">{t('suppliers.linkedProducts', 'Linked Products')}</div>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <ShoppingCart className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <div className="text-2xl font-bold">{supplier.total_orders}</div>
              <div className="text-sm text-gray-500">{t('suppliers.totalOrders', 'Total Orders')}</div>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <CheckCircle className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <div className="text-2xl font-bold">
                {supplier.total_orders > 0
                  ? `${Math.round((supplier.successful_orders / supplier.total_orders) * 100)}%`
                  : 'N/A'}
              </div>
              <div className="text-sm text-gray-500">{t('suppliers.successRate', 'Success Rate')}</div>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Clock className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <div className="text-2xl font-bold">
                {supplier.average_fulfillment_days
                  ? `${Math.round(supplier.average_fulfillment_days)} days`
                  : 'N/A'}
              </div>
              <div className="text-sm text-gray-500">{t('suppliers.avgFulfillment', 'Avg. Fulfillment')}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-6">
          {TABS.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pb-3 px-1 font-medium text-sm border-b-2 transition-colors ${
                activeTab === tab
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab === 'details' && t('suppliers.details', 'Details')}
              {tab === 'products' && t('suppliers.products', 'Products')}
              {tab === 'orders' && t('suppliers.orders', 'Orders')}
              {tab === 'settings' && t('suppliers.settings', 'Settings')}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        {activeTab === 'details' && (
          <div className="space-y-6">
            {/* Basic Info */}
            <div>
              <h3 className="text-lg font-semibold mb-4">{t('suppliers.basicInfo', 'Basic Information')}</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('suppliers.supplierName', 'Supplier Name')} *
                  </label>
                  <input
                    type="text"
                    value={formData.name || ''}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('suppliers.supplierCode', 'Supplier Code')}
                  </label>
                  <input
                    type="text"
                    value={formData.code || ''}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('suppliers.supplierType', 'Supplier Type')}
                  </label>
                  <select
                    value={formData.supplier_type || 'dropship'}
                    onChange={(e) => setFormData({ ...formData, supplier_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="dropship">Dropship</option>
                    <option value="wholesale">Wholesale</option>
                    <option value="manufacturer">Manufacturer</option>
                    <option value="print_on_demand">Print on Demand</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('suppliers.status', 'Status')}
                  </label>
                  <select
                    value={formData.status || 'active'}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="active">{t('suppliers.active', 'Active')}</option>
                    <option value="inactive">{t('suppliers.inactive', 'Inactive')}</option>
                    <option value="pending">{t('suppliers.pending', 'Pending')}</option>
                    <option value="suspended">{t('suppliers.suspended', 'Suspended')}</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="text-lg font-semibold mb-4">{t('suppliers.contactInfo', 'Contact Information')}</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('suppliers.email', 'Email')}
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="email"
                      value={formData.email || ''}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('suppliers.phone', 'Phone')}
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="tel"
                      value={formData.phone || ''}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('suppliers.website', 'Website')}
                  </label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="url"
                      value={formData.website || ''}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('suppliers.contactName', 'Contact Person')}
                  </label>
                  <input
                    type="text"
                    value={formData.contact_name || ''}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Address */}
            <div>
              <h3 className="text-lg font-semibold mb-4">{t('suppliers.address', 'Address')}</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('suppliers.addressLine1', 'Address Line 1')}
                  </label>
                  <input
                    type="text"
                    value={formData.address_line1 || ''}
                    onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('suppliers.city', 'City')}
                  </label>
                  <input
                    type="text"
                    value={formData.city || ''}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('suppliers.country', 'Country')}
                  </label>
                  <select
                    value={formData.country || ''}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">{t('suppliers.selectCountry', 'Select country')}</option>
                    {COUNTRIES.map((c) => (
                      <option key={c.code} value={c.code}>{c.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('suppliers.notes', 'Notes')}
              </label>
              <textarea
                value={formData.notes || ''}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        )}

        {activeTab === 'products' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{t('suppliers.linkedProducts', 'Linked Products')}</h3>
              <button
                onClick={() => setShowLinkModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Plus className="w-4 h-4" />
                {t('suppliers.linkProduct', 'Link Product')}
              </button>
            </div>

            {loadingProducts ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              </div>
            ) : supplierProducts.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">{t('suppliers.noLinkedProducts', 'No products linked to this supplier')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {supplierProducts.map((sp) => (
                  <div
                    key={sp.id}
                    className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gray-100 rounded-lg overflow-hidden">
                        {sp.product.images[0] ? (
                          <img src={sp.product.images[0]} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <Package className="w-full h-full p-2 text-gray-400" />
                        )}
                      </div>
                      <div>
                        <div className="font-medium">{sp.product.name}</div>
                        <div className="text-sm text-gray-500 flex items-center gap-3">
                          {sp.supplier_sku && <span>SKU: {sp.supplier_sku}</span>}
                          <span>Cost: {formatCurrency(sp.supplier_cost)}</span>
                          <span>Sell: {formatCurrency(sp.product.price)}</span>
                          {sp.profit_margin !== null && (
                            <span className={sp.profit_margin > 0 ? 'text-green-600' : 'text-red-600'}>
                              Profit: {formatCurrency(sp.profit_margin)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {sp.is_primary && (
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">Primary</span>
                      )}
                      {sp.auto_fulfill && (
                        <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full flex items-center gap-1">
                          <Zap className="w-3 h-3" /> Auto
                        </span>
                      )}
                      <button
                        onClick={() => handleUnlinkProduct(sp.product_id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        title={t('suppliers.unlinkProduct', 'Unlink product')}
                      >
                        <Unlink className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'orders' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{t('suppliers.recentOrders', 'Recent Orders')}</h3>
              <Link
                to={`/store/${storeId}/suppliers/${supplierId}/orders`}
                className="text-blue-600 hover:text-blue-700 text-sm flex items-center gap-1"
              >
                {t('suppliers.viewAllOrders', 'View all orders')}
                <ExternalLink className="w-4 h-4" />
              </Link>
            </div>
            {supplier.recent_orders.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <Truck className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">{t('suppliers.noOrders', 'No orders yet')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {supplier.recent_orders.map((order) => (
                  <div
                    key={order.id}
                    className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
                  >
                    <div>
                      <div className="font-medium">
                        {order.supplier_order_number || order.id.slice(0, 8)}
                      </div>
                      <div className="text-sm text-gray-500">
                        {new Date(order.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        order.status === 'delivered' ? 'bg-green-100 text-green-800' :
                        order.status === 'shipped' ? 'bg-blue-100 text-blue-800' :
                        order.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {order.status}
                      </span>
                      <span className="font-medium">{formatCurrency(order.total_cost)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="space-y-6">
            {/* Pricing Settings */}
            <div>
              <h3 className="text-lg font-semibold mb-4">{t('suppliers.pricingSettings', 'Pricing Settings')}</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('suppliers.currency', 'Currency')}
                  </label>
                  <select
                    value={formData.currency || 'USD'}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="ILS">ILS</option>
                    <option value="JPY">JPY</option>
                    <option value="CNY">CNY</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('suppliers.defaultMarkupType', 'Default Markup Type')}
                  </label>
                  <select
                    value={formData.default_markup_type || 'percentage'}
                    onChange={(e) => setFormData({ ...formData, default_markup_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="percentage">{t('suppliers.percentage', 'Percentage')}</option>
                    <option value="fixed">{t('suppliers.fixed', 'Fixed Amount')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('suppliers.defaultMarkupValue', 'Default Markup Value')}
                  </label>
                  <input
                    type="number"
                    value={formData.default_markup_value || 0}
                    onChange={(e) => setFormData({ ...formData, default_markup_value: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Fulfillment Settings */}
            <div>
              <h3 className="text-lg font-semibold mb-4">{t('suppliers.fulfillmentSettings', 'Fulfillment Settings')}</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('suppliers.avgProcessingDays', 'Average Processing Days')}
                  </label>
                  <input
                    type="number"
                    value={formData.average_processing_days || 1}
                    onChange={(e) => setFormData({ ...formData, average_processing_days: parseInt(e.target.value) || 1 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('suppliers.avgShippingDays', 'Average Shipping Days')}
                  </label>
                  <input
                    type="number"
                    value={formData.average_shipping_days || 5}
                    onChange={(e) => setFormData({ ...formData, average_shipping_days: parseInt(e.target.value) || 5 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('suppliers.orderPrefix', 'Order Prefix')}
                  </label>
                  <input
                    type="text"
                    value={formData.order_prefix || ''}
                    onChange={(e) => setFormData({ ...formData, order_prefix: e.target.value })}
                    placeholder="SO"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('suppliers.shipsFromCountry', 'Ships From Country')}
                  </label>
                  <select
                    value={formData.ships_from_country || ''}
                    onChange={(e) => setFormData({ ...formData, ships_from_country: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">{t('suppliers.selectCountry', 'Select country')}</option>
                    {COUNTRIES.map((c) => (
                      <option key={c.code} value={c.code}>{c.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Auto-Order Settings */}
            <div>
              <h3 className="text-lg font-semibold mb-4">{t('suppliers.autoOrderSettings', 'Auto-Order Settings')}</h3>
              <div className="space-y-4">
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={formData.auto_order_enabled || false}
                    onChange={(e) => setFormData({ ...formData, auto_order_enabled: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    {t('suppliers.enableAutoOrder', 'Enable automatic order submission')}
                  </span>
                </label>
                {formData.auto_order_enabled && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('suppliers.autoOrderDelay', 'Delay before auto-ordering (minutes)')}
                    </label>
                    <input
                      type="number"
                      value={formData.auto_order_delay_minutes || 0}
                      onChange={(e) => setFormData({ ...formData, auto_order_delay_minutes: parseInt(e.target.value) || 0 })}
                      className="w-48 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Link Product Modal */}
      {showLinkModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-semibold mb-4">{t('suppliers.linkProduct', 'Link Product')}</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('suppliers.selectProduct', 'Select Product')} *
                </label>
                <select
                  value={selectedProduct}
                  onChange={(e) => setSelectedProduct(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">{t('suppliers.chooseProduct', 'Choose a product...')}</option>
                  {availableProducts.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('suppliers.supplierSku', 'Supplier SKU')}
                </label>
                <input
                  type="text"
                  value={linkFormData.supplier_sku}
                  onChange={(e) => setLinkFormData({ ...linkFormData, supplier_sku: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('suppliers.supplierCost', 'Supplier Cost (cents)')} *
                  </label>
                  <input
                    type="number"
                    value={linkFormData.supplier_cost}
                    onChange={(e) => setLinkFormData({ ...linkFormData, supplier_cost: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('suppliers.shippingCost', 'Shipping Cost (cents)')}
                  </label>
                  <input
                    type="number"
                    value={linkFormData.shipping_cost}
                    onChange={(e) => setLinkFormData({ ...linkFormData, shipping_cost: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="flex gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={linkFormData.is_primary}
                    onChange={(e) => setLinkFormData({ ...linkFormData, is_primary: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600"
                  />
                  <span className="text-sm">{t('suppliers.primarySupplier', 'Primary Supplier')}</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={linkFormData.auto_fulfill}
                    onChange={(e) => setLinkFormData({ ...linkFormData, auto_fulfill: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600"
                  />
                  <span className="text-sm">{t('suppliers.autoFulfill', 'Auto-Fulfill')}</span>
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowLinkModal(false);
                  setSelectedProduct('');
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={handleLinkProduct}
                disabled={saving || !selectedProduct || !linkFormData.supplier_cost}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? t('common.saving', 'Saving...') : t('suppliers.linkProduct', 'Link Product')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
